/**
 * Glossary page config — the hardcoded half of the hybrid /glossary page.
 *
 * The *terms* live in Sanity (document type `glossaryTerm`, editable in Studio
 * like blog posts). This file holds the parts that stay in code: the category
 * set, the hero and CTA copy, and the shared TypeScript shapes that both the
 * Sanity fetch layer and the page components consume.
 *
 * `GlossaryTerm` is the app-facing shape returned by `getGlossaryTerms()` — the
 * Sanity `related` references are dereferenced to slug strings in GROQ, so the
 * fetched objects match this interface exactly. Two conventions enforced by the
 * schema stay load-bearing for SEO: every `definition` opens by restating the
 * term (lifted verbatim into DefinedTerm JSON-LD and quoted standalone by AI
 * Overviews), and `link` points at the service page the term implies, so a
 * "what is replatforming" visitor reaches /shopify-experts/shopify-migration.
 */

export type GlossaryCategoryId = 'shopify' | 'ecommerce' | 'seo' | 'migration';

export interface GlossaryCategory {
  id: GlossaryCategoryId;
  /** Chip label in the filter bar. */
  label: string;
}

export interface GlossaryTerm {
  /** Anchor id on /glossary today; the URL segment if terms ever get their own pages. */
  slug: string;
  term: string;
  /** Expansions and synonyms. Rendered under the term and matched by search. */
  aka?: string[];
  category: GlossaryCategoryId;
  /** 40–80 words. First sentence must restate the term — see file header. */
  definition: string;
  /** Slugs of other terms. Rendered as in-page anchor links. */
  related?: string[];
  /** Optional link to the service page this term maps to. */
  link?: { href: string; label: string };
}

export const GLOSSARY_CATEGORIES: readonly GlossaryCategory[] = [
  { id: 'shopify', label: 'Shopify platform' },
  { id: 'ecommerce', label: 'Ecommerce & CRO' },
  { id: 'seo', label: 'SEO & performance' },
  { id: 'migration', label: 'Migration & integrations' },
] as const;

export const GLOSSARY_PAGE = {
  eyebrow: 'Reference',
  heading: {
    line1: 'Shopify',
    line2Accent: 'glossary',
    line3: 'for merchants',
  },
  description:
    'The vocabulary of Shopify and ecommerce, defined in plain English — from Liquid and metaobjects to AOV, Core Web Vitals, and replatforming. Written by the EVDEV team from the work we do for merchants every day.',
  searchLabel: 'Search terms',
  searchPlaceholder: 'Search a term, e.g. metafields, AOV, LCP…',
  allCategoriesLabel: 'All terms',
  emptyHeading: 'No terms match that search.',
  emptyBody: 'Try a shorter search, or clear the filters to see all terms.',
  clearLabel: 'Clear filters',
  relatedLabel: 'See also',
} as const;

export const GLOSSARY_CTA = {
  eyebrow: 'Know the words, need the work done?',
  heading: 'Let’s put the vocabulary to use.',
  body: 'Free 30-minute consultation with a Shopify team that has shipped the migrations, the headless builds, and the Core Web Vitals fixes on this page.',
  label: 'Start your Shopify project',
  // Deep-page CTAs land on the dedicated /contacts page, not the homepage
  // anchor (finding conversion-n09tbe; destination per conversion-fqcyg1).
  href: '/contacts',
} as const;

/**
 * Glossary terms that are indexable on their own page (/glossary/{slug}).
 * Every other term, and the /glossary hub, stays noindex,follow. These are
 * Shopify-platform terms with search demand and low difficulty (DataForSEO,
 * US, Oct 2026: metaobjects KD 4, checkout extensibility KD 16, hydrogen KD 19,
 * webhooks KD 21, metafields KD 23, flow KD 27, markets KD 31), each rewritten
 * to a full explanation in Sanity before it was added here. Adding a slug
 * without a full `body` in Sanity puts a thin page in the index.
 */
export interface GlossaryIndexedTerm {
  /** Full <title>, brand suffix included. */
  title: string;
  description: string;
}

export const GLOSSARY_INDEXED_TERMS: Readonly<Record<string, GlossaryIndexedTerm>> = {
  metafields: {
    title: "Shopify Metafields: What They Are, How to Use Them | EVDEV",
    description:
      "How Shopify metafields work in 2026: types, current limits, setup steps, filters, Liquid access, when to use a metaobject instead, and common mistakes.",
  },
  metaobjects: {
    title: "Shopify Metaobjects: What They Are, How to Use Them | EVDEV",
    description:
      "How Shopify metaobjects work today: limits, web pages, Liquid and API access, when to pick a metaobject over a metafield, setup steps and common mistakes.",
  },
  'checkout-extensibility': {
    title: "Shopify Checkout Extensibility Explained (2026) | EVDEV",
    description:
      "What Shopify Checkout Extensibility is, what replaced checkout.liquid and Scripts, plan limits, migration steps and the mistakes that cost conversions.",
  },
  'shopify-flow': {
    title: "Shopify Flow: What It Is and How to Use It | EVDEV",
    description:
      "How Shopify Flow works in 2026: plans, triggers and actions, current limits, Flow vs Functions, setup steps and the mistakes that break store automations.",
  },
  hydrogen: {
    title: "What Is Shopify Hydrogen? 2026-10 Explained | EVDEV",
    description:
      "Shopify Hydrogen 2026-10 explained: what the headless library does, what it leaves to you, Oxygen hosting and limits, setup steps and mistakes to avoid.",
  },
  webhook: {
    title: "What Is a Shopify Webhook? Delivery and HMAC | EVDEV",
    description:
      "How Shopify webhooks work: topics, delivery rules, 5-second timeout, 8 retries over 4 hours, HMAC verification, mandatory topics and when to use Events.",
  },
  'shopify-markets': {
    title: "What Is Shopify Markets? International Selling | EVDEV",
    description:
      "Shopify Markets explained: market types and inheritance, subfolders vs domains for SEO, duties at checkout, B2B catalogue limits and Managed Markets rules.",
  },
};

/** When the indexed term pages were last rewritten, for the sitemap. */
export const GLOSSARY_INDEXED_UPDATED = '2026-10-23';
